import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { GraduationCap, CheckCircle2, ArrowRight, Lightbulb, Briefcase, Star, Compass } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PageHero } from "@/components/PageHero";
import { RelatedLinks } from "@/components/RelatedLinks";
import { usePageMeta } from "@/hooks/usePageMeta";
import { SITE } from "@/data/site";

const BENEFITS = [
  { icon: Star, title: "UCAS-recognised credits", desc: "Train in the studio while earning credits that count toward higher education." },
  { icon: Briefcase, title: "Industry mentorship", desc: "Direct access to working choreographers, directors and educators." },
  { icon: Compass, title: "Live performance placements", desc: "Real stages, festivals and tours across the UK as part of your training." },
  { icon: Lightbulb, title: "Career-ready pathways", desc: "Choreography, teaching, creative direction, production and beyond." },
];

const PATH = [
  { code: "L2", label: "Foundation Diploma", desc: "Year 1 · Technique, repertoire & stage craft." },
  { code: "L3", label: "Extended Diploma", desc: "Years 2–3 · UCAS-accredited pre-degree training." },
  { code: "L4", label: "HND in Creative Practice", desc: "Year 4 · Industry-led specialism." },
  { code: "L6", label: "BA (Hons) Top-up", desc: "Final year · Full degree status." },
];

export default function BAPathwayPage() {
  usePageMeta(
    "BA Degree Pathway · Level 2 to BA (Hons)",
    "USDD Academy's BA (Hons) pathway in development — from Level 2 foundation diploma to a full bachelor's degree without leaving the studio. Register your interest."
  );
  return (
    <>
      <Header />
      <main>
        <PageHero
          overline="07 · BA Degree Pathway"
          title="Dance can be"
          highlight="your degree."
          description="From Level 2 to a full BA (Hons) — in partnership with validated UK universities. The USDD Academy BA Pathway is being developed for launch. Register your interest now to secure early access and prospectus updates."
          ctaPrimary={{ label: "Register your interest", to: "/contact" }}
          ctaSecondary={{ label: "See qualifications", to: "/training" }}
          bg={SITE.pathwayBg}
        />

        {/* Benefits */}
        <section className="py-20 sm:py-24 bg-black">
          <div className="max-w-7xl mx-auto px-6 sm:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12 pb-8 border-b border-white/10">
              <div className="lg:col-span-7">
                <div className="flex items-center gap-3 mb-4">
                  <span className="h-px w-10 bg-brand-yellow" />
                  <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-yellow">Why the BA Pathway</span>
                </div>
                <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white tracking-wide leading-[0.95]">
                  A real degree. <br /><span className="text-brand-yellow text-glow-yellow">Built around dance.</span>
                </h2>
              </div>
              <div className="lg:col-span-5 lg:pt-6">
                <p className="text-zinc-300/90 text-base sm:text-lg leading-relaxed">
                  No more choosing between training and study. The BA Pathway lets serious
                  dancers progress from foundation to honours, with mentorship from
                  working industry artists at every stage.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {BENEFITS.map((b, i) => (
                <motion.div
                  key={b.title}
                  data-testid={`ba-benefit-${i}`}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="p-6 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-brand-yellow/40 transition-all"
                >
                  <b.icon className="text-brand-yellow mb-4" size={28} />
                  <h3 className="font-display text-xl text-white tracking-wide">{b.title}</h3>
                  <p className="mt-2 text-xs text-zinc-400 leading-relaxed">{b.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Progression map */}
        <section className="py-20 sm:py-24 bg-gradient-to-b from-black via-zinc-950 to-black">
          <div className="max-w-5xl mx-auto px-6 sm:px-8">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-brand-yellow" />
              <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-yellow">Your progression</span>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl text-white tracking-wide leading-[0.95] mb-12">
              From first step to <span className="text-brand-yellow">degree</span>.
            </h2>

            <div className="relative p-8 sm:p-12 rounded-3xl bg-gradient-to-br from-brand-purple/40 via-brand-purpleDeep/40 to-black border border-brand-purpleLight/30 backdrop-blur-xl">
              <GraduationCap className="text-brand-yellow mb-8" size={44} />
              <div className="space-y-6">
                {PATH.map((s, i) => (
                  <motion.div
                    key={s.code}
                    data-testid={`ba-path-step-${i}`}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.45, delay: i * 0.1 }}
                    className="flex items-start gap-5 sm:gap-7 group"
                  >
                    <div className="font-display font-black text-3xl sm:text-4xl text-brand-yellow w-16 sm:w-20 leading-none">{s.code}</div>
                    <div className="flex-1 pb-5 border-b border-white/10 group-last:border-b-0">
                      <div className="font-display text-xl sm:text-2xl text-white tracking-wide leading-tight">{s.label}</div>
                      <div className="text-sm text-zinc-400 mt-1">{s.desc}</div>
                    </div>
                  </motion.div>
                ))}
              </div>
              <div className="mt-10 pt-6 border-t border-white/10 text-xs uppercase tracking-[0.3em] text-zinc-400">
                ~4 years · Full pathway · Launching soon
              </div>
            </div>
          </div>
        </section>

        {/* Register interest CTA */}
        <section className="py-20 bg-black">
          <div className="max-w-5xl mx-auto px-6 sm:px-8">
            <div className="relative overflow-hidden rounded-3xl border border-brand-yellow/30 bg-gradient-to-br from-brand-purpleDeep via-black to-black p-8 sm:p-12">
              <div className="absolute -top-20 -right-20 w-[300px] h-[300px] rounded-full bg-brand-yellow/15 blur-[100px]" />
              <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                <div className="lg:col-span-7">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-brand-yellow mb-3">Be first in line</div>
                  <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white tracking-wide leading-[0.95]">
                    Register your <span className="text-brand-yellow text-glow-yellow">interest</span>.
                  </h3>
                  <p className="mt-4 text-sm sm:text-base text-zinc-300 max-w-xl">
                    Get prospectus updates, early-access enrolment windows and the
                    chance to shape the programme as a founding cohort.
                  </p>
                  <ul className="mt-5 space-y-2 text-sm text-zinc-300">
                    {["Prospectus & curriculum updates", "Founding cohort priority", "Open day invitations"].map((x) => (
                      <li key={x} className="flex items-center gap-2"><CheckCircle2 size={16} className="text-brand-yellow" /> {x}</li>
                    ))}
                  </ul>
                </div>
                <div className="lg:col-span-5 flex flex-col gap-3">
                  <Link
                    to="/contact"
                    data-testid="ba-register-interest-btn"
                    className="inline-flex items-center justify-between gap-3 px-6 py-4 rounded-full bg-brand-yellow text-black font-black uppercase text-sm tracking-wider hover:bg-brand-yellowHover hover:scale-[1.02] transition-all glow-yellow"
                  >
                    Register interest <ArrowRight size={16} />
                  </Link>
                  <Link
                    to="/training"
                    data-testid="ba-explore-training-btn"
                    className="inline-flex items-center justify-between gap-3 px-6 py-4 rounded-full border border-white/25 bg-white/5 backdrop-blur text-white font-bold uppercase text-sm tracking-wider hover:bg-white hover:text-black hover:border-white transition-all"
                  >
                    Explore current training <ArrowRight size={16} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <RelatedLinks
          title="Related pathways"
          items={[
            { tag: "Adults", label: "Weekly Training", desc: "Hip-Hop, Afro Dance, Dancehall and more", to: "/training" },
            { tag: "Schools", label: "Schools & Organisations", desc: "Bespoke residencies & workshops", to: "/schools-organisations" },
            { tag: "Kids", label: "USDD Kids", desc: "Junior programme ages 6–17", to: "/kids" },
          ]}
        />
      </main>
      <Footer />
    </>
  );
}
